import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Pencil, Trash2, Plus, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useSiteData } from "../../context/SiteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "", destination: "", duration: "", price: "", sale_price: "",
  image: "", images_text: "", itinerary_text: "", faqs: [], description: "", published: true,
};

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium outline-none transition focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20";

const TourFormModal = ({ tour, onClose, onSaved }) => {
  const [form, setForm] = useState(
    tour
      ? {
          ...EMPTY,
          ...tour,
          sale_price: tour.sale_price ?? "",
          images_text: (tour.images || []).join("\n"),
          itinerary_text: (tour.itinerary || []).join("\n"),
          faqs: (tour.faqs || []).map((f) => ({ ...f })),
        }
      : EMPTY
  );
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const setFaq = (i, k) => (e) =>
    setForm({ ...form, faqs: form.faqs.map((f, j) => (j === i ? { ...f, [k]: e.target.value } : f)) });
  const addFaq = () => setForm({ ...form, faqs: [...form.faqs, { question: "", answer: "" }] });
  const removeFaq = (i) => setForm({ ...form, faqs: form.faqs.filter((_, j) => j !== i) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.image.trim() || !form.price) {
      toast.error("Name, price and image URL are required.");
      return;
    }
    setBusy(true);
    const payload = {
      ...form,
      price: Number(form.price),
      sale_price: form.sale_price ? Number(form.sale_price) : null,
      images: form.images_text.split("\n").map((s) => s.trim()).filter(Boolean),
      itinerary: form.itinerary_text.split("\n").map((s) => s.trim()).filter(Boolean),
      faqs: form.faqs.filter((f) => f.question.trim() && f.answer.trim()),
    };
    delete payload.images_text;
    delete payload.itinerary_text;
    delete payload.slug;
    try {
      if (tour) {
        await axios.put(`${API}/tours/${tour.id}`, payload, { withCredentials: true });
        toast.success(`${form.name} updated — live on the site.`);
      } else {
        await axios.post(`${API}/tours`, payload, { withCredentials: true });
        toast.success(`${form.name} added to the tours.`);
      }
      onSaved();
      onClose();
    } catch {
      toast.error("Could not save the tour. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0b132b]/50 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="tour-form-modal-overlay"
    >
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.97 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-white p-7 shadow-2xl"
        data-testid="tour-form-modal"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-xl text-[#0b132b]">{tour ? "Edit Tour" : "Add Tour"}</h3>
          <button data-testid="tour-form-close-button" onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3.5" data-testid="tour-form">
          <input data-testid="tour-form-name-input" value={form.name} onChange={set("name")} placeholder="Tour name *" className={inputCls} />
          <div className="grid grid-cols-2 gap-3.5">
            <input data-testid="tour-form-destination-input" value={form.destination} onChange={set("destination")} placeholder="Destination (e.g. Zermatt)" className={inputCls} />
            <input data-testid="tour-form-duration-input" value={form.duration} onChange={set("duration")} placeholder="Duration (e.g. 2 days)" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3.5">
            <input data-testid="tour-form-price-input" type="number" min="1" value={form.price} onChange={set("price")} placeholder="Price / person *" className={inputCls} />
            <input data-testid="tour-form-sale-price-input" type="number" min="0" value={form.sale_price} onChange={set("sale_price")} placeholder="Sale price (optional)" className={inputCls} />
          </div>
          <textarea data-testid="tour-form-description-input" rows={3} value={form.description} onChange={set("description")} placeholder="Short description" className={`${inputCls} resize-none`} />
          <textarea
            data-testid="tour-form-itinerary-input"
            rows={3}
            value={form.itinerary_text}
            onChange={set("itinerary_text")}
            placeholder="Itinerary — one stop per line (optional)"
            className={`${inputCls} resize-none`}
          />
          <input data-testid="tour-form-image-input" value={form.image} onChange={set("image")} placeholder="Main image URL *" className={inputCls} />
          <div className="rounded-2xl border border-slate-200 p-3.5 space-y-3" data-testid="tour-form-faqs">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">FAQs (shown on the tour page)</p>
              <button
                type="button"
                data-testid="tour-form-add-faq-button"
                onClick={addFaq}
                className="inline-flex items-center gap-1 rounded-full bg-[var(--brand-soft)] px-3 py-1.5 text-xs font-bold text-[var(--brand)] hover:bg-[var(--brand)] hover:text-white transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Add question
              </button>
            </div>
            {form.faqs.length === 0 && (
              <p className="text-xs text-slate-400">No questions yet — add one to help guests decide.</p>
            )}
            {form.faqs.map((faq, i) => (
              <div key={i} className="rounded-xl bg-slate-50 p-3 space-y-2" data-testid={`tour-form-faq-${i}`}>
                <div className="flex items-center gap-2">
                  <input
                    data-testid={`tour-form-faq-question-${i}`}
                    value={faq.question}
                    onChange={setFaq(i, "question")}
                    placeholder={`Question ${i + 1}`}
                    className={inputCls}
                  />
                  <button
                    type="button"
                    data-testid={`tour-form-faq-remove-${i}`}
                    onClick={() => removeFaq(i)}
                    className="w-9 h-9 shrink-0 rounded-full bg-red-50 flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                    aria-label={`Remove question ${i + 1}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <textarea
                  data-testid={`tour-form-faq-answer-${i}`}
                  rows={2}
                  value={faq.answer}
                  onChange={setFaq(i, "answer")}
                  placeholder="Answer"
                  className={`${inputCls} resize-none`}
                />
              </div>
            ))}
          </div>
          <textarea
            data-testid="tour-form-gallery-input"
            rows={3}
            value={form.images_text}
            onChange={set("images_text")}
            placeholder="Gallery image URLs — one per line (optional)"
            className={`${inputCls} resize-none`}
          />
          {form.image && (
            <img src={form.image} alt="Tour preview" className="w-full h-36 object-cover rounded-xl border border-slate-100" data-testid="tour-form-image-preview" />
          )}
          <label className="flex items-center gap-3 cursor-pointer" data-testid="tour-form-published-toggle">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
              className="w-5 h-5 rounded accent-[var(--brand)]"
              data-testid="tour-form-published-checkbox"
            />
            <span className="text-sm font-bold text-[#0b132b]">Published — visible on the website</span>
          </label>
          <button
            data-testid="tour-form-save-button"
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-[var(--brand)] text-white text-sm font-bold py-3 shadow-lg shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] disabled:opacity-60"
          >
            {busy ? "Saving…" : tour ? "Save Changes" : "Add Tour"}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
};

export const ToursTab = () => {
  const { refreshTours, fmtPrice } = useSiteData();
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);

  const load = () =>
    axios
      .get(`${API}/tours/all`, { withCredentials: true })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));

  useEffect(() => {
    load();
  }, []);

  const afterChange = () => {
    load();
    refreshTours();
  };

  const togglePublish = async (tour) => {
    try {
      await axios.put(
        `${API}/tours/${tour.id}`,
        {
          name: tour.name,
          destination: tour.destination,
          duration: tour.duration,
          price: tour.price,
          sale_price: tour.sale_price ?? null,
          image: tour.image,
          images: tour.images || [],
          itinerary: tour.itinerary || [],
          faqs: tour.faqs || [],
          description: tour.description || "",
          published: !tour.published,
        },
        { withCredentials: true }
      );
      toast.success(tour.published ? `${tour.name} unpublished — hidden from the site.` : `${tour.name} published — live on the site.`);
      afterChange();
    } catch {
      toast.error("Could not update the tour.");
    }
  };

  const remove = async (tour) => {
    if (!window.confirm(`Delete ${tour.name}?`)) return;
    try {
      await axios.delete(`${API}/tours/${tour.id}`, { withCredentials: true });
      toast.success(`${tour.name} removed.`);
      afterChange();
    } catch {
      toast.error("Could not delete the tour.");
    }
  };

  if (rows === null) {
    return <div className="w-10 h-10 rounded-full border-4 border-[var(--brand)]/20 border-t-[var(--brand)] animate-spin" data-testid="tours-loading-spinner" />;
  }

  return (
    <div data-testid="admin-tours-tab">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm font-semibold text-slate-500">
          {rows.filter((t) => t.published).length} published · {rows.filter((t) => !t.published).length} unpublished
        </p>
        <button
          data-testid="admin-add-tour-button"
          onClick={() => {
            setIsNew(true);
            setEditing({});
          }}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-5 py-2.5 shadow-lg shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)]"
        >
          <Plus className="w-4 h-4" /> Add Tour
        </button>
      </div>

      <div className="grid gap-4" data-testid="admin-tours-list">
        {rows.map((tour) => (
          <div
            key={tour.id}
            data-testid={`admin-tour-row-${tour.id}`}
            className={`flex items-center gap-4 rounded-2xl bg-white border p-4 shadow-[0_10px_30px_-16px_rgba(11,19,43,0.15)] transition-opacity ${
              tour.published ? "border-slate-100" : "border-dashed border-slate-300 opacity-70"
            }`}
          >
            <img src={tour.image} alt={tour.name} className="w-20 h-14 rounded-xl object-cover shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-display font-bold text-[#0b132b] truncate flex items-center gap-2">
                {tour.name}
                {!tour.published && (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">Draft</span>
                )}
              </p>
              <p className="text-xs font-semibold text-slate-400">
                {tour.destination} · {tour.duration}
                {tour.sale_price ? ` · SALE ${fmtPrice(tour.sale_price)}` : ""}
              </p>
            </div>
            <p className="font-display font-extrabold text-[var(--brand)] shrink-0">{fmtPrice(tour.price)}<span className="text-[11px] text-slate-400 font-semibold">/person</span></p>
            <button
              data-testid={`admin-tour-publish-${tour.id}`}
              onClick={() => togglePublish(tour)}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                tour.published ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white" : "bg-slate-100 text-slate-400 hover:bg-slate-600 hover:text-white"
              }`}
              aria-label={tour.published ? `Unpublish ${tour.name}` : `Publish ${tour.name}`}
            >
              {tour.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
            <button
              data-testid={`admin-tour-edit-${tour.id}`}
              onClick={() => {
                setIsNew(false);
                setEditing(tour);
              }}
              className="w-9 h-9 rounded-full bg-[var(--brand-soft)] flex items-center justify-center text-[var(--brand)] hover:bg-[var(--brand)] hover:text-white transition-colors"
              aria-label={`Edit ${tour.name}`}
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              data-testid={`admin-tour-delete-${tour.id}`}
              onClick={() => remove(tour)}
              className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white transition-colors"
              aria-label={`Delete ${tour.name}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {editing && (
          <TourFormModal
            tour={isNew ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={afterChange}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
